import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { PowerMap } from '../../components/power/PowerMap';
import { PowerNearby, RecentReports } from '../../components/power/PowerPanels';
import { ReportPowerSheet } from '../../components/power/ReportPowerSheet';
import { EVIDENCE_LABEL, POWER_SOURCE_LABEL, powerLocationById } from '../../data/power';
import { freshnessLabel } from '../../utils/power';
import { PowerStatusPill } from '../../components/power/PowerPrimitives';
type View = 'map' | 'nearby' | 'reports';

/**
 * The three full surfaces the Power page links into. One component, because the
 * map, the nearby list and the report log are the same records at different
 * lengths — duplicating them would let them disagree.
 */
export function PowerBrowse() {
  const {
    pathname
  } = useLocation();
  const view: View = pathname.endsWith('/nearby') ? 'nearby' : pathname.endsWith('/reports') ? 'reports' : 'map';
  const {
    campusLocationId,
    reports,
    getNearbyStatuses
  } = usePower();
  const [selected, setSelected] = useState<string | null>(campusLocationId);
  const [reporting, setReporting] = useState(false);
  const titles: Record<View, {
    title: string;
    description: string;
  }> = {
    map: {
      title: 'Power map',
      description: 'Every mapped location on your campus.'
    },
    nearby: {
      title: 'Power near me',
      description: 'Mapped locations within walking distance of your accommodation.'
    },
    reports: {
      title: 'Power reports',
      description: 'The evidence behind every status, newest first.'
    }
  };
  const nearby = getNearbyStatuses(campusLocationId, 3);
  return <div className="space-y-5" data-id="element-11733" data-mp="158a4ozyvh572">
      <Link to="/power" className="inline-flex min-h-touch items-center gap-1.5 text-body-sm font-medium text-brand-700 hover:underline" data-id="element-11734" data-mp="158ihkruzwai9">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden data-id="element-11735" data-mp="2uq73t9rkypl9" />
        Back to Power
      </Link>
      <PageHeader eyebrow="Campuzon Power" title={titles[view].title} description={titles[view].description} documentTitle data-id="element-11736" data-mp="2zhfahy1izwuq" />

      {view === 'map' && <PowerMap selectedId={selected} onSelect={setSelected} height="h-[32rem]" data-id="element-11737" data-mp="2pqugknwsrgi5" />}

      {view === 'nearby' && <div className="grid gap-4 lg:grid-cols-2" data-id="element-11738" data-mp="3dhzrconvrill">
          <PowerNearby limit={20} data-id="element-11739" data-mp="3ig1b7bt4rly4" />
          <section className="rounded-2xl border border-line bg-surface p-5 shadow-panel" data-id="element-11740" data-mp="3ieymnaja5vld">
            <h2 className="text-h4 font-semibold text-ink" data-id="element-11741" data-mp="34h1o48l9nf42">Within 3 km</h2>
            <ul className="mt-3 divide-y divide-line" data-id="element-11742" data-mp="39fbmes2aevtw">
              {nearby.map(entry => <li key={entry.location.id} className="flex items-center gap-3 py-2.5" data-id="element-11743" data-mp="1t139zqb8e2vz">
                  <span className="min-w-0 flex-1" data-id="element-11744" data-mp="1t3fzcojbjfj2">
                    <span className="block truncate text-body-sm font-medium text-ink" data-id="element-11745" data-mp="1y0y03rn6mofz">
                      {entry.location.name}
                    </span>
                    <span className="cz-num block text-caption text-ink-subtle" data-id="element-11746" data-mp="1lhub4r0cpml6">
                      {entry.distanceKm.toFixed(1)} km · {POWER_SOURCE_LABEL[entry.status.source]}
                    </span>
                  </span>
                  <PowerStatusPill state={entry.status.state} size="xs" data-id="element-11747" data-mp="1ujaa82lpu2a3" />
                </li>)}
            </ul>
          </section>
        </div>}

      {view === 'reports' && <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]" data-id="element-11748" data-mp="11uml1vxuisr9">
          <RecentReports limit={8} data-id="element-11749" data-mp="1gizkj2w56tqi" />
          <section className="rounded-2xl border border-line bg-surface p-5 shadow-panel" data-id="element-11750" data-mp="1gk26cwely5zk">
            <h2 className="text-h4 font-semibold text-ink" data-id="element-11751" data-mp="1bqzf4uxaz8mj">Full report log</h2>
            <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-11752" data-mp="o1ev08pey5bo">
              Each row is one report. Reporters are described by kind, never named.
            </p>
            <ul className="mt-3 divide-y divide-line" data-id="element-11753" data-mp="3ih2z1orylj3l">
              {reports.filter(report => !powerLocationById(report.locationId)?.personal).map(report => <li key={report.id} className="flex items-center gap-3 py-2.5" data-id="element-11754" data-mp="3rhsrqjlxatd3">
                    <span className="min-w-0 flex-1" data-id="element-11755" data-mp="3rfy3owfcul4s">
                      <span className="block truncate text-body-sm font-medium text-ink" data-id="element-11756" data-mp="451tpmjx4d4">
                        {powerLocationById(report.locationId)?.name}
                      </span>
                      <span className="block truncate text-caption text-ink-subtle" data-id="element-11757" data-mp="a0hvwom0dk8e">
                        {report.own ? 'You' : EVIDENCE_LABEL[report.evidence]} ·{' '}
                        {POWER_SOURCE_LABEL[report.source]}
                      </span>
                    </span>
                    <PowerStatusPill state={report.state} size="xs" data-id="element-11758" data-mp="2gs8x5yg7tdmn" />
                    <span className="cz-num shrink-0 text-caption text-ink-subtle" data-id="element-11759" data-mp="2gu3llmqp3nvh">
                      {freshnessLabel(report.reportedAt)}
                    </span>
                  </li>)}
            </ul>
          </section>
        </div>}

      <button type="button" onClick={() => setReporting(true)} className="min-h-touch rounded-xl bg-brand-700 px-4 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800" data-id="element-11760" data-mp="28ibdgiaij797">
        Report power
      </button>

      <ReportPowerSheet open={reporting} onClose={() => setReporting(false)} presetLocationId={selected} data-id="element-11761" data-mp="2w7mf01zot5oe" />
    </div>;
}